import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import type { SlideDeckMaterial } from '@breakoutlearning/firebase-repository/models/SlideDeckMaterial'
import { CourseMaterialListItem } from 'components/slide-deck-material-components/CourseMaterialListItem'
import { CourseMaterialsInstructionsMarkdown } from 'components/CourseMaterialsInstructionsMarkdown'
import { Spinner } from 'components/Spinner'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { observer } from 'mobx-react-lite'
import { Fragment, useEffect, useId, useMemo } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import { sortPreWorkMaterials } from 'util/preWorkMaterials'

// Shared with the admin participant-view preview (CourseMaterialsPreviewDialog).
export function PreWorkModalBody({
  headingId,
  slideDeckId,
  slideDeckName,
  slideDeckVersion,
  instructions,
  loading,
  materials,
}: {
  headingId: string
  slideDeckId: string
  slideDeckName: string
  slideDeckVersion: string
  instructions?: string
  loading: boolean
  materials: SlideDeckMaterial[]
}) {
  const { tt } = useTranslationTyped()
  return (
    <div className="max-h-[80vh]">
      <h1 id={headingId} className="mb-2 text-headline-large">
        {tt.student_assignment.prework()}
      </h1>
      {instructions ? (
        <div className="mb-5">
          <CourseMaterialsInstructionsMarkdown>
            {instructions}
          </CourseMaterialsInstructionsMarkdown>
        </div>
      ) : (
        <p className="mb-5 text-body-large text-on-surface-var">
          {tt.student_assignment.prework_description()}
        </p>
      )}
      {loading ? (
        <div className="flex grow items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div>
          {materials.map((material) => {
            const { isPodcast, isPDF } = material
            const renderLabel = isPodcast || isPDF
            const label =
              renderLabel &&
              (isPodcast
                ? tt.student_assignment.prework_listen_to_podcast()
                : tt.student_assignment.prework_read_case_summary())
            return (
              <Fragment key={material.id}>
                {label && (
                  <div className="mb-3 text-body-medium text-grey-text">
                    {label}
                  </div>
                )}
                <CourseMaterialListItem
                  slideDeckVersion={slideDeckVersion}
                  className="mb-5 flex h-[62px] flex-row items-center justify-between gap-4 rounded-2xl bg-light-grey p-4"
                  slideDeckName={slideDeckName}
                  slideDeckId={slideDeckId}
                  material={material}
                />
              </Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}

export const PreWorkModal = observer(function PreWorkModal({
  cubit,
}: {
  cubit: StudentAssignmentCubit
}) {
  const headingId = useId()
  // Re-fetch materials if any signed URL has expired (6h TTL)
  useEffect(() => {
    cubit.ensureFreshMaterialUrls()
  }, [cubit])

  const materials = cubit.availableStudentMaterials
  const libraryObject = cubit.libraryObject
  const preWorkMaterials = useMemo(
    () => sortPreWorkMaterials(materials),
    [materials]
  )

  const { slideDeck } = libraryObject

  return (
    <Dialog size="sm" aria-labelledby={headingId} testId="prework-modal">
      <DialogCloseButton />
      <PreWorkModalBody
        headingId={headingId}
        slideDeckId={slideDeck.id}
        slideDeckName={slideDeck.data.slideDeckName}
        slideDeckVersion={slideDeck.data.slideDeckVersion}
        instructions={slideDeck.data.slideDeckCourseMaterialsInstructions?.trim()}
        loading={
          cubit.materials.isLoading ||
          cubit.isRefreshingMaterials ||
          libraryObject.slideDeck.isLoading
        }
        materials={preWorkMaterials}
      />
    </Dialog>
  )
})
